import { API } from '../config';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  ChevronLeft, Phone, KeyRound, Lock, LogOut, RefreshCw, Wifi, AlertTriangle, Plus, Check, Trash2
} from 'lucide-react';
import { toast } from 'sonner';


export default function TelegramUserLogin() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('check');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [tempSessionId, setTempSessionId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const [statusRes, accountsRes] = await Promise.all([
        axios.get(`${API}/telegram-user/status`, { headers: getAuthHeaders() }),
        axios.get(`${API}/telegram-user/accounts`, { headers: getAuthHeaders() }).catch(() => ({ data: { accounts: [] } }))
      ]);
      setStatus(statusRes.data);
      setAccounts(accountsRes.data.accounts || statusRes.data.accounts || []);
      if (!statusRes.data.available) setStep('unavailable');
      else setStep('accounts');
    } catch { toast.error('Durum alınamadı'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStatus(); }, []); // eslint-disable-line

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/telegram-user/send-code`, { phone: phone.trim() }, { headers: getAuthHeaders() });
      setTempSessionId(res.data.temp_session_id);
      toast.success('Doğrulama kodu gönderildi');
      setStep('code');
    } catch (err) { toast.error(err.response?.data?.detail || 'Kod gönderilemedi'); }
    finally { setActionLoading(false); }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/telegram-user/verify-code`, { phone, code: code.trim(), temp_session_id: tempSessionId }, { headers: getAuthHeaders() });
      if (res.data.needs_2fa) { setStep('2fa'); toast.info('İki adımlı doğrulama gerekli'); }
      else { toast.success('Telegram hesabı bağlandı!'); resetAndRefresh(); }
    } catch (err) {
      const d = err.response?.data;
      if (d?.needs_2fa) setStep('2fa');
      else toast.error(d?.detail || 'Doğrulanamadı');
    } finally { setActionLoading(false); }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/telegram-user/verify-2fa`, { phone, password: password.trim(), temp_session_id: tempSessionId }, { headers: getAuthHeaders() });
      toast.success('Telegram hesabı bağlandı!');
      resetAndRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Şifre doğrulanamadı'); }
    finally { setActionLoading(false); }
  };

  const handleRemove = async (sid) => {
    try {
      await axios.delete(`${API}/telegram-user/accounts/${sid}`, { headers: getAuthHeaders() });
      toast.success('Hesap kaldırıldı');
      fetchStatus();
    } catch { toast.error('Kaldırılamadı'); }
  };

  const handleSetActive = async (sid) => {
    try {
      await axios.post(`${API}/telegram-user/set-active/${sid}`, {}, { headers: getAuthHeaders() });
      toast.success('Aktif hesap değiştirildi');
      fetchStatus();
    } catch { toast.error('Değiştirilemedi'); }
  };

  const resetAndRefresh = () => {
    setPhone(''); setCode(''); setPassword(''); setTempSessionId('');
    setStep('accounts');
    fetchStatus();
  };

  const activeAccounts = accounts.filter(a => a.status === 'active');

  return (
    <div className="min-h-dvh bg-background">
      <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border/70 px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => navigate('/app')}><ChevronLeft className="w-5 h-5" /></Button>
        <Phone className="w-5 h-5 text-primary" />
        <h1 className="text-lg font-semibold font-display">Telegram Hesaplarım</h1>
        <Badge variant="secondary" className="text-[10px] ml-auto">{activeAccounts.length}/10</Badge>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Yükleniyor...</div>
        ) : step === 'unavailable' ? (
          <div className="bg-card rounded-xl border border-border/70 p-6 text-center space-y-4">
            <AlertTriangle className="w-10 h-10 text-muted-foreground mx-auto" />
            <h2 className="text-lg font-semibold">Yapılandırılmamış</h2>
            <p className="text-sm text-muted-foreground">Telegram kullanıcı girişi şu anda yapılandırılmamış.</p>
            <Button variant="outline" className="rounded-xl" onClick={() => navigate('/app')}>Geri Dön</Button>
          </div>
        ) : step === 'accounts' ? (
          <>
            {/* Account List */}
            <div className="space-y-2">
              <h2 className="text-sm font-medium text-foreground">Bağlı Hesaplar</h2>
              {activeAccounts.length === 0 ? (
                <div className="bg-card rounded-xl border border-border/70 p-6 text-center">
                  <p className="text-sm text-muted-foreground mb-3">Henüz bağlı Telegram hesabınız yok.</p>
                </div>
              ) : (
                activeAccounts.map(acc => (
                  <div key={acc.id} className="bg-card rounded-xl border border-border/70 p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${acc.is_active_account ? 'bg-green-500/20' : 'bg-secondary'}`}>
                      <Phone className={`w-5 h-5 ${acc.is_active_account ? 'text-green-500' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {acc.tg_first_name || ''} {acc.tg_last_name || ''} {acc.tg_username ? `(@${acc.tg_username})` : ''}
                      </p>
                      <p className="text-[10px] text-muted-foreground">+{acc.tg_phone || acc.phone || '?'}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        {acc.is_active_account && <Badge className="text-[9px] h-4 bg-green-500 text-white">Aktif</Badge>}
                        <Badge variant="secondary" className="text-[9px] h-4">{acc.status === 'active' ? 'Bağlı' : acc.status}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {!acc.is_active_account && (
                        <Button variant="outline" size="sm" className="h-7 text-[10px] rounded-lg" onClick={() => handleSetActive(acc.id)}>
                          <Check className="w-3 h-3 mr-0.5" /> Geçiş
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-destructive" onClick={() => handleRemove(acc.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Add New Account Button */}
            {activeAccounts.length < 10 && (
              <Button className="w-full rounded-xl" onClick={() => setStep('phone')}>
                <Plus className="w-4 h-4 mr-2" /> Yeni Telegram Hesabı Ekle
              </Button>
            )}
            {activeAccounts.length >= 10 && (
              <p className="text-xs text-destructive text-center">En fazla 10 Telegram hesabı ekleyebilirsiniz.</p>
            )}
          </>
        ) : (
          /* LOGIN FLOW: phone / code / 2fa */
          <div className="bg-card rounded-xl border border-border/70 p-6 space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                {step === 'phone' ? <Phone className="w-5 h-5 text-primary" /> :
                 step === 'code' ? <KeyRound className="w-5 h-5 text-primary" /> :
                 <Lock className="w-5 h-5 text-primary" />}
              </div>
              <div>
                <h2 className="text-sm font-semibold font-display text-foreground">
                  {step === 'phone' ? 'Telefon Numarası' : step === 'code' ? 'Doğrulama Kodu' : 'İki Adımlı Doğrulama'}
                </h2>
                <p className="text-[10px] text-muted-foreground">
                  {step === 'phone' ? 'Telegram hesabınıza bağlı telefon numarasını girin' :
                   step === 'code' ? 'Telegram uygulamasına gelen kodu girin' : 'Telegram şifrenizi girin'}
                </p>
              </div>
            </div>

            {step === 'phone' && (
              <form onSubmit={handleSendCode} className="space-y-3">
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+90 5XX XXX XX XX"
                  className="rounded-xl text-lg text-foreground" required />
                <Button type="submit" className="w-full rounded-xl h-11" disabled={actionLoading || !phone.trim()}>
                  {actionLoading ? 'Gönderiliyor...' : 'Kod Gönder'}
                </Button>
                <Button type="button" variant="ghost" className="w-full rounded-xl text-xs" onClick={() => setStep('accounts')}>İptal</Button>
              </form>
            )}
            {step === 'code' && (
              <form onSubmit={handleVerifyCode} className="space-y-3">
                <Input value={code} onChange={e => setCode(e.target.value)} placeholder="12345"
                  className="rounded-xl text-lg text-center tracking-widest text-foreground" required autoFocus />
                <Button type="submit" className="w-full rounded-xl h-11" disabled={actionLoading || !code.trim()}>
                  {actionLoading ? 'Doğrulanıyor...' : 'Doğrula'}
                </Button>
                <Button type="button" variant="ghost" className="w-full rounded-xl text-xs" onClick={() => { setStep('phone'); setCode(''); }}>Geri</Button>
              </form>
            )}
            {step === '2fa' && (
              <form onSubmit={handleVerify2FA} className="space-y-3">
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Şifrenizi girin"
                  className="rounded-xl text-foreground" required autoFocus />
                <Button type="submit" className="w-full rounded-xl h-11" disabled={actionLoading || !password.trim()}>
                  {actionLoading ? 'Doğrulanıyor...' : 'Giriş Yap'}
                </Button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
